import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu } from "lucide-react";
import { useShop, api } from "@/store";
import { toast } from "sonner";

export const Mark = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <rect x="16" y="16" width="32" height="32" transform="rotate(45 32 32)" fill="none" stroke="var(--terra)" strokeWidth="4" />
    <rect x="26.5" y="26.5" width="11" height="11" transform="rotate(45 32 32)" fill="var(--terra)" />
  </svg>
);

export function Marquee() {
  const { t } = useShop();
  const items = t("tick");
  const seq = [...items, ...items, ...items, ...items];
  return (
    <div className="ticker" aria-hidden="true">
      <div className="tk">
        {[0, 1].map((half) => (
          <span key={half} style={{ display: "inline-flex", alignItems: "center", padding: 0 }}>
            {seq.map((s, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", padding: 0 }}>
                <span>{s}</span>
                <i />
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}

const NAV = ["shop", "story", "artisans", "impact", "journal", "wholesale"];

export function Header() {
  const { t, lang, setLang, cur, setCur, cartCount, wish, setDrawer } = useShop();
  const loc = useLocation();
  return (
    <header className="site">
      <div className="wrap nav">
        <button className="icon burger" data-testid="mobile-menu-button" aria-label="Menu" onClick={() => setDrawer({ type: "mobile" })}>
          <Menu />
        </button>
        <nav className="links" aria-label="Primary">
          {NAV.map((k) => (
            <NavLink key={k} to={`/${k}`} className={loc.pathname === `/${k}` ? "on" : ""} data-testid={`nav-${k}`}>
              {t(`nav.${k}`)}
            </NavLink>
          ))}
        </nav>
        <Link className="logo" to="/" data-testid="nav-logo" aria-label="Nattiva home">
          <Mark /> Nattiva
        </Link>
        <div className="tools">
          <div className="lang hid" role="group" aria-label="Language">
            <button data-testid="lang-en-button" className={lang === "en" ? "on" : ""} onClick={() => setLang("en")}>EN</button>
            <button data-testid="lang-es-button" className={lang === "es" ? "on" : ""} onClick={() => setLang("es")}>ES</button>
          </div>
          <select className="sel" data-testid="currency-toggle-select" aria-label="Currency" style={{ border: 0, paddingRight: 18, fontSize: 11 }} value={cur} onChange={(e) => setCur(e.target.value)}>
            <option>USD</option>
            <option>EUR</option>
            <option>COP</option>
          </select>
          <button className="icon" data-testid="search-drawer-trigger" aria-label="Search" onClick={() => setDrawer({ type: "search" })}>
            <Search />
          </button>
          <button className="icon" data-testid="wishlist-drawer-trigger" aria-label="Wishlist" onClick={() => setDrawer({ type: "wish" })}>
            <Heart />
            {wish.length > 0 && <span className="badge" data-testid="wishlist-badge">{wish.length}</span>}
          </button>
          <Link className="icon" to="/account" aria-label="Account" data-testid="account-link">
            <User />
          </Link>
          <button className="icon" data-testid="cart-drawer-trigger" aria-label="Cart" onClick={() => setDrawer({ type: "cart" })}>
            <ShoppingBag />
            {cartCount > 0 && <span className="badge" data-testid="cart-badge">{cartCount}</span>}
          </button>
        </div>
      </div>
    </header>
  );
}

export function Newsletter() {
  const { t } = useShop();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api("/newsletter", { method: "POST", body: JSON.stringify({ email }) });
      toast(t("news.ok"));
      setEmail("");
    } catch {
      toast(t("news.err"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="news">
      <div className="wrap in">
        <div>
          <div className="eyebrow">{t("news.eb")}</div>
          <h3>{t("news.h")}</h3>
          <p>{t("news.p")}</p>
        </div>
        <form onSubmit={submit} data-testid="newsletter-form">
          <input type="email" required aria-label="Email" data-testid="newsletter-email-input" placeholder={t("news.ph")} value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" data-testid="newsletter-submit" disabled={busy}>{t("news.btn")}</button>
        </form>
      </div>
    </section>
  );
}

export function Footer() {
  const { t, theme, setTheme } = useShop();
  return (
    <footer className="site">
      <div className="wrap">
        <div className="fgrid">
          <div>
            <Link className="logo" style={{ fontSize: 24 }} to="/"><Mark size={16} /> Nattiva</Link>
            <p style={{ marginTop: 12, maxWidth: "30ch", color: "var(--fg-2)" }}>{t("foot.blurb")}</p>
            <div className="social">
              <a href="https://instagram.com" aria-label="Instagram" data-testid="social-instagram"><svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r=".8" /></svg></a>
              <a href="https://pinterest.com" aria-label="Pinterest" data-testid="social-pinterest"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M10 20l2-8m0 0c-2 0-3-1.5-3-3a3 3 0 0 1 6 0c0 2-1 4-3 4" /></svg></a>
              <a href="https://tiktok.com" aria-label="TikTok" data-testid="social-tiktok"><svg viewBox="0 0 24 24"><path d="M14 4v10a3 3 0 1 1-3-3" /><path d="M14 4c0 2.5 2 4.5 4.5 4.5" /></svg></a>
              <a href="https://youtube.com" aria-label="YouTube" data-testid="social-youtube"><svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="3" /><path d="m10 9 5 3-5 3z" /></svg></a>
            </div>
          </div>
          <div>
            <b>{t("foot.shop")}</b>
            <Link to="/shop">{t("nav.shop")}</Link>
            <Link to="/shop?f=one">{t("foot.one")}</Link>
            <Link to="/shop?f=new">{t("foot.new")}</Link>
            <Link to="/gifting">{t("foot.gift")}</Link>
          </div>
          <div>
            <b>{t("foot.about")}</b>
            <Link to="/story">{t("nav.story")}</Link>
            <Link to="/artisans">{t("nav.artisans")}</Link>
            <Link to="/impact">{t("nav.impact")}</Link>
            <Link to="/journal">{t("nav.journal")}</Link>
            <Link to="/press">{t("foot.press")}</Link>
            <Link to="/careers">{t("foot.careers")}</Link>
          </div>
          <div>
            <b>{t("foot.care")}</b>
            <Link to="/help">{t("foot.help")}</Link>
            <Link to="/help#ship">{t("foot.ship")}</Link>
            <Link to="/help#returns">{t("foot.returns")}</Link>
            <Link to="/help#careguide">{t("foot.careguide")}</Link>
            <Link to="/impact#verify">{t("foot.verify")}</Link>
            <Link to="/contact">{t("foot.contact")}</Link>
          </div>
          <div>
            <b>{t("foot.biz")}</b>
            <Link to="/wholesale">{t("foot.wholesale")}</Link>
            <Link to="/gifting">{t("foot.corp")}</Link>
            <Link to="/investors">{t("foot.investors")}</Link>
            <Link to="/help#legal">{t("foot.privacy")}</Link>
            <Link to="/help#legal">{t("foot.terms")}</Link>
          </div>
        </div>
        <div className="fbottom">
          <span>© 2026 Nattiva LLC · {t("foot.made")}</span>
          <div className="pay"><span>Visa</span><span>Mastercard</span><span>Amex</span><span>Apple Pay</span><span>PayPal</span><span>Klarna</span></div>
          <span>
            <a href="#theme" data-testid="theme-toggle-button" onClick={(e) => { e.preventDefault(); setTheme(theme === "dark" ? "light" : "dark"); }}>{t("foot.theme")}</a>
            {" · "}{t("foot.a11y")}
          </span>
        </div>
      </div>
    </footer>
  );
}

export function CookieBanner() {
  const { t } = useShop();
  const [show, setShow] = useState(() => {
    try { return !localStorage.getItem("nattiva.cookie"); } catch { return false; }
  });
  if (!show) return null;
  const pick = (v) => {
    try { localStorage.setItem("nattiva.cookie", JSON.stringify(v)); } catch {}
    setShow(false);
  };
  return (
    <div className="cookie" role="dialog" data-testid="cookie-banner">
      <p>{t("cookie.t")}</p>
      <div className="acts">
        <button className="btn primary" data-testid="cookie-accept" onClick={() => pick("all")}>{t("cookie.ok")}</button>
        <button className="btn" data-testid="cookie-essential" onClick={() => pick("essential")}>{t("cookie.no")}</button>
      </div>
    </div>
  );
}
